import React, { useCallback, useState } from 'react';
import { Button, Divider, Spinner } from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import {
  Drawer, DrawerBody, DrawerFooter, DrawerHeader, DrawerHeaderTitle,
} from '@fluentui/react-drawer';
import { useEvent } from '@bingads-webui-react/event-provider';
import { useDataSelection } from '@bingads-webui-react/data-selection';
import { useDataSource } from '@bingads-webui-react/data-source';

import { I18n, SelectionFlatTreeProps } from '../types';
import { Paginator } from '../components/paginator';
import { SearchBox } from '../components/searchbox';
import { SelectionCount } from '../components/selection-count';
import { ConfirmButton } from '../components/confirm-button';
import { Spacer } from '../components/spacer';
import { useAggregatedData } from '../context';
import { useDrawerContainerStyles } from './drawer-container-styles';
import { SelectOnlySwitch } from '../components';
import NoSearchResultsMessage from '../components/no-search-results-message';
import NoSelectionsMessage from '../components/no-selections-message';
import { useTreeData } from '../hooks/use-tree-data';

function getStatusMessage({
  isLoading,
  hasSearchTerm,
  hasItems,
  hasDataSelection,
  showSelectedOnly,
  i18n,
}) {
  if (isLoading) {
    return i18n.getString(_TL_('Loading...'));
  }
  if (hasSearchTerm && !hasItems) {
    return i18n.getString(_TL_('No search results found.'));
  }
  if (!hasSearchTerm && !hasDataSelection && showSelectedOnly) {
    return i18n.getString(_TL_('No selections.'));
  }
  return '';
}

export interface DrawerContainerProps {
  i18n: I18n;
  titleText: string;
  confirmButtonText?: string;
  selectionMode: SelectionFlatTreeProps['selectionMode'];
  open?: boolean;
  paginate?: boolean;
  customFilter?: (item: any, search: string) => boolean;
  onConfirm?: (dataSelection: any[]) => void;
  onOpenChange?: (open: boolean) => void;
  onRenderItemContent?: SelectionFlatTreeProps['onRenderContent'];
  children?: any;
  className?: string;
}

export const DrawerContainer: React.FC<DrawerContainerProps> = (props) => {
  const {
    i18n, titleText, confirmButtonText, selectionMode, open, paginate, className, customFilter, onConfirm, onOpenChange, onRenderItemContent, children,
  } = props;
  const classes = useDrawerContainerStyles();
  const { isLoading } = useAggregatedData();
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const { dataSelection } = useDataSelection();
  const { searchTerm } = useDataSource();

  const { items } = useTreeData({
    mode: 'page',
    showSelectedOnly,
    customFilter,
  });
  const hasSearchTerm = searchTerm.trim().length > 0;
  const hasItems = items.length > 0;
  const hasDataSelection = dataSelection.length > 0;
  const statusMessage = getStatusMessage({
    isLoading,
    hasSearchTerm,
    hasItems,
    hasDataSelection,
    showSelectedOnly,
    i18n,
  });

  const openDrawer = useCallback(() => {
    onOpenChange?.(true);
  }, [onOpenChange]);

  const closeDrawer = useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  useEvent('open-drawer', openDrawer);
  useEvent('close-drawer', closeDrawer);

  return (
    <Drawer
      type="overlay"
      position="end"
      size="medium"
      open={open}
      onOpenChange={closeDrawer}
      className={className}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={(
            <Button
              appearance="subtle"
              icon={<Dismiss24Regular />}
              onClick={closeDrawer}
            />
          )}
        >
          {titleText}
        </DrawerHeaderTitle>
        <div className={classes.controls}>
          <SearchBox
            className={classes.searchbox}
            appearance="filled-darker"
          />
          <SelectOnlySwitch
            i18n={i18n}
            showSelectedOnly={showSelectedOnly}
            onChange={() => setShowSelectedOnly((prev) => !prev)}
          />
        </div>
      </DrawerHeader>
      <Divider className={classes.divider} />
      <DrawerBody>
        {isLoading && <Spinner className={classes.spinner} size="extra-tiny" label={i18n.getString(_TL_('Loading...'))} />}
        {!isLoading && hasSearchTerm && !hasItems && <NoSearchResultsMessage showSelectedOnly={showSelectedOnly} i18n={i18n} /> }
        {!isLoading && !hasSearchTerm && !hasDataSelection && showSelectedOnly && <NoSelectionsMessage i18n={i18n} /> }
        {!isLoading && React.Children.map(children, (child) => React.cloneElement(child, {
          i18n,
          className: classes.tree,
          selectionMode,
          aggregateMode: 'page',
          showSelectedOnly,
          customFilter,
          onRenderItemContent,
        }))}
        {/* for a11y announce do not remove the div below */}
        <div
          id="a11yAnnounceArea"
          aria-live="polite"
          aria-atomic="true"
          className={classes.hiddenA11yAnnounceArea}
        >
          {statusMessage}
        </div>
      </DrawerBody>
      <Divider className={classes.divider} />
      <DrawerFooter className={classes.footer}>
        <ConfirmButton
          label={confirmButtonText ?? i18n.getString(_TL_('Save'))}
          onConfirm={onConfirm}
        />
        <SelectionCount i18n={i18n} />
        <Spacer />
        {paginate && <Paginator i18n={i18n} />}
      </DrawerFooter>
    </Drawer>
  );
};
